import { defineStore } from 'pinia';
import type {
  IAPICartResponseData,
  ICartLine,
  ICartItem,
} from '~/modules/cart/types/cart-types';
import convertCartLineToOrder from '~/modules/checkout/utils/ConvertCartLineToOrder';

export const useCartStore = defineStore('cartStore', () => {
  const cartItems = reactive<ICartItem[]>([]);
  const total = ref(0);
  const subtotal = ref(0);
  const quantity = ref(0);
  const discountPercent = ref(0);
  const discountPrice = ref(0);
  const promocode = ref('');
  const availableBonusCount = ref(0);
  const accruedBonusCount = ref(0);
  const spentBonusCount = ref(0);

  const isCartLoading = ref(false);
  const isPromocodeApplied = ref(false);
  const isCartEmpty = computed(() => cartItems.length === 0);

  const isAllItemsDisabled = computed(() => cartItems.filter((item) => item.checked && !item.removed).length === 0);
  const isItemChecked = (item: ICartItem) => isAllItemsDisabled.value || item.checked;
  const isItemActive = (item: ICartItem) => isItemChecked(item) && !item.removed && item.line.available;
  const activeCartLines = computed(() => cartItems.filter(isItemActive).map((item) => item.line));
  const activeOrderLines = computed(() => activeCartLines.value.map(convertCartLineToOrder));

  const isItemAvailable = (item: ICartItem) => item.line.available;
  const availableItems = computed(() => cartItems.filter(isItemAvailable));
  const unavailableItems = computed(() => cartItems.filter((item: ICartItem) => !isItemAvailable(item)));

  const transformLines = (lines: ICartLine[]): ICartItem[] => {

    return lines.map((line) => {
      const index = getItemIndex(line.id);
      const checked = cartItems[index]
        ? cartItems[index].checked
        : true;

      return {
        id: line.id,
        removed: false,
        checked,
        line,
      };
    });
  };

  const getItemIndex = (id: string | number) => cartItems.findIndex((item: ICartItem) => item.id === id);

  const initCart = (data: IAPICartResponseData) => {
    updateCartCounters(data);
    initCartItems(data.lines);
  };

  const initCartItems = (lines: ICartLine[]) => {
    const items = transformLines(lines);

    cartItems.length = 0;
    cartItems.push(...items);
  };

  const updateCartItem = (id: string | number, line: ICartLine) => {
    const index = getItemIndex(id);
    if (index === -1) return;

    cartItems[index].id = line.id;
    cartItems[index].line = line;
  };

  const updateCartCounters = (data: IAPICartResponseData) => {
    if (data.total_amount) {
      total.value = Number(data.total_amount.amount);
    }

    if (data.subtotal_amount) {
      subtotal.value = Number(data.subtotal_amount.amount);
    }

    if (data.total_quantity) {
      quantity.value = Number(data.total_quantity.amount);
    }

    if (data.promo_code) {
      promocode.value = data.promo_code.value;
      isPromocodeApplied.value = true;
    } else if (data.promo_code === null) {
      isPromocodeApplied.value = false;
    }

    if (data.discount?.amount) {
      discountPrice.value = Number(data.discount.amount);
    } else {
      discountPrice.value = 0;
    }

    availableBonusCount.value = data.available_bonus_count ?? 0;
    accruedBonusCount.value = data.accrued_bonus_count ?? 0;
    spentBonusCount.value = data.spent_bonus_count ?? 0;
  };

  return {
    activeCartLines,
    activeOrderLines,
    cartItems,
    availableItems,
    unavailableItems,
    isCartLoading,
    isCartEmpty,
    isPromocodeApplied,
    total,
    subtotal,
    quantity,
    discountPercent,
    discountPrice,
    promocode,
    availableBonusCount,
    accruedBonusCount,
    spentBonusCount,
    initCart,
    updateCartCounters,
    updateCartItem,
    initCartItems,
    getItemIndex,
  };
});
